import { EnergyBlockType, LegacyEnergyLevelType, ENERGY_TYPE_MAPPING, EnergyDataPoint, UserEnergySettings, Chronotype, EnergyLevel, BaselineInput, SleepData } from './types';

/**
 * Convert BaselineInput to SleepData format for the new algorithm
 */
export function convertBaselineInputToSleepData(input: BaselineInput): SleepData {
  const now = new Date();
  
  // Parse wake time
  let wakeTime = new Date();
  if (input.wakeTimeHour !== undefined) {
    wakeTime.setHours(input.wakeTimeHour, 0, 0, 0);
  } else if (input.wakeTime) {
    const [hours, minutes] = input.wakeTime.split(':').map(Number);
    wakeTime.setHours(hours, minutes || 0, 0, 0);
  } else {
    wakeTime.setHours(7, 0, 0, 0); // Default 7 AM
  }

  // Calculate bedtime based on sleep duration or sleep time
  let bedtime = new Date(wakeTime);
  const sleepDuration = input.sleepDuration || 8;
  
  if (input.sleepTime) {
    const [hours, minutes] = input.sleepTime.split(':').map(Number);
    bedtime.setHours(hours, minutes || 0, 0, 0);
    // If sleep time is after wake time, it's the previous day
    if (bedtime > wakeTime) {
      bedtime.setDate(bedtime.getDate() - 1);
    }
  } else {
    bedtime.setTime(wakeTime.getTime() - sleepDuration * 60 * 60 * 1000);
  }

  return {
    wakeTime,
    bedtime,
    sleepDuration,
    sleepEfficiency: 90, // Default good efficiency
    sleepDebt: 0, // Default no debt
    sleepQuality: 8, // Default good quality
    nightWakings: 0,
    sleepLatency: 10
  };
}

/**
 * Convert energy array to EnergyDataPoint format
 */
export function convertToEnergyDataPoints(energyArray: number[]): EnergyDataPoint[] {
  return energyArray.map((energy, index) => ({
    time: index.toString(),
    value: energy,
    hour: index,
    level: energy,
    energy: energy,
    type: getEnergyTypeForValue(energy),
    timestamp: new Date()
  }));
}

/**
 * Convert EnergyLevel to simple number for backward compatibility
 */
export function convertEnergyLevelToNumber(energyLevel: EnergyLevel | number): number {
  if (typeof energyLevel === 'number') {
    return energyLevel;
  }
  return energyLevel.energy || energyLevel.value || 50;
}

/**
 * Convert energy preference/type to the expected format
 * This is a critical function used throughout the Chronocal app
 */
export function convertEnergyPreference(energyType: LegacyEnergyLevelType | string): EnergyBlockType {
  // Handle string inputs
  const normalizedType = energyType.toLowerCase() as LegacyEnergyLevelType;

  // Use the mapping from types.ts
  return ENERGY_TYPE_MAPPING[normalizedType] || 'focus';
}

/**
 * Convert energy zone to legacy format for backwards compatibility
 */
export function convertEnergyZoneToLegacy(blockType: EnergyBlockType): LegacyEnergyLevelType {
  switch (blockType) {
    case 'power':
      return 'high';
    case 'focus':
      return 'medium';
    case 'drift':
      return 'low';
    default:
      return 'medium';
  }
}

/**
 * Convert legacy energy type to block type
 */
export function convertLegacyToEnergyZone(legacyType: LegacyEnergyLevelType): EnergyBlockType {
  return ENERGY_TYPE_MAPPING[legacyType] || 'focus';
}

/**
 * Get current energy level for a specific time
 */
export function getCurrentEnergy(
  energyForecast: number[] | EnergyDataPoint[],
  time: Date = new Date(),
  settings?: UserEnergySettings
): { level: LegacyEnergyLevelType; percentage: number } {
  const hour = time.getHours();
  
  let energyValue = 50; // Default
  
  if (Array.isArray(energyForecast)) {
    if (typeof energyForecast[0] === 'number') {
      // Array of numbers
      energyValue = (energyForecast as number[])[hour] || 50;
    } else {
      // Array of EnergyDataPoint
      const dataPoint = (energyForecast as EnergyDataPoint[]).find(dp => 
        (dp.hour === hour) || (dp.time === hour.toString())
      );
      energyValue = dataPoint?.value || dataPoint?.energy || 50;
    }
  }

  // Convert to legacy format
  let level: LegacyEnergyLevelType = 'medium';
  if (energyValue >= 80) level = 'high';
  else if (energyValue >= 60) level = 'medium';
  else level = 'low';

  return { level, percentage: energyValue };
}

/**
 * Get energy chart data for visualization
 */
export function getEnergyChartData(
  date: Date = new Date(),
  settings?: UserEnergySettings
): EnergyDataPoint[] {
  const data: EnergyDataPoint[] = [];

  for (let hour = 0; hour < 24; hour++) {
    const time = new Date(date);
    time.setHours(hour, 0, 0, 0);

    const energy = getCurrentEnergyForHour(hour, settings);

    data.push({
      time: hour.toString(),
      value: energy,
      hour,
      level: energy,
      type: getEnergyTypeForValue(energy)
    });
  }

  return data;
}

/**
 * Get current energy for a specific hour (internal helper)
 */
function getCurrentEnergyForHour(hour: number, settings?: UserEnergySettings): number {
  // Basic circadian pattern - can be enhanced with user settings
  if (hour >= 6 && hour <= 9) {
    return 65; // Morning rise
  } else if (hour >= 10 && hour <= 12) {
    return 85; // Morning peak
  } else if (hour >= 13 && hour <= 15) {
    return 55; // Afternoon dip
  } else if (hour >= 16 && hour <= 18) {
    return 75; // Evening recovery
  } else if (hour >= 19 && hour <= 21) {
    return 60; // Evening decline
  } else {
    return 25; // Sleep/rest hours
  }
}

/**
 * Get energy type for a given energy value
 */
export function getEnergyTypeForValue(energy: number): EnergyBlockType {
  if (energy >= 80) return 'power';
  if (energy >= 60) return 'focus';
  if (energy >= 30) return 'drift';
  return 'rest';
}

// Additional utility functions for compatibility with new implementation

/**
 * Convert energy value to block type
 */
export function energyToBlock(energy: number): string {
  return getEnergyTypeForValue(energy);
}

/**
 * Convert energy zone type
 */
export function convertEnergyZone(blockType: string): { type: string; energy: number } {
  const type = blockType as EnergyBlockType;
  let energy = 50; // Default

  switch (type) {
    case 'power':
      energy = 85;
      break;
    case 'focus':
      energy = 70;
      break;
    case 'drift':
      energy = 45;
      break;
    case 'rest':
      energy = 25;
      break;
  }

  return { type, energy };
}

/**
 * Calculate average energy from energy levels
 */
export function calculateAverageEnergy(energyLevels: EnergyLevel[]): number {
  if (!energyLevels || energyLevels.length === 0) return 0;

  const sum = energyLevels.reduce((acc, level) => acc + (level.energy || level.value || 0), 0);
  return sum / energyLevels.length;
}

/**
 * Find peak energy times
 */
export function findPeakEnergyTimes(energyLevels: EnergyLevel[]): Date[] {
  if (!energyLevels || energyLevels.length === 0) return [];

  const peaks: Date[] = [];
  const threshold = Math.max(...energyLevels.map(e => e.energy || e.value || 0)) * 0.8;

  for (let i = 1; i < energyLevels.length - 1; i++) {
    const current = energyLevels[i];
    const prev = energyLevels[i - 1];
    const next = energyLevels[i + 1];
    
    const currentVal = current.energy || current.value || 0;
    const prevVal = prev.energy || prev.value || 0;
    const nextVal = next.energy || next.value || 0;

    if (currentVal > prevVal && currentVal > nextVal && currentVal >= threshold) {
      peaks.push(current.timestamp);
    }
  }

  return peaks;
}

/**
 * Format energy level for display
 */
export function formatEnergyLevel(energy: EnergyLevel | number): string {
  const value = typeof energy === 'number' ? energy : (energy.energy || energy.value || 0);
  return `${Math.round(value)}%`;
}

/**
 * Parse chronotype from string
 */
export function parseChronotype(chronotypeStr: string): Chronotype {
  const normalized = chronotypeStr.toUpperCase();
  switch (normalized) {
    case 'BEAR':
      return Chronotype.BEAR;
    case 'LION':
      return Chronotype.LION;
    case 'WOLF':
      return Chronotype.WOLF;
    case 'DOLPHIN':
      return Chronotype.DOLPHIN;
    default:
      return Chronotype.BEAR; // Default to most common
  }
}

/**
 * Check if time is in sleep window
 */
export function isInSleepWindow(time: Date, chronotype: Chronotype): boolean {
  const hour = time.getHours();

  switch (chronotype) {
    case Chronotype.LION:
      return hour <= 5 || hour >= 21; // Early sleeper
    case Chronotype.BEAR:
      return hour <= 6 || hour >= 23; // Normal schedule
    case Chronotype.WOLF:
      return hour <= 8 || hour >= 24 || hour <= 1; // Late sleeper
    case Chronotype.DOLPHIN:
      return hour <= 6 || hour >= 23; // Light sleeper, similar to bear
    default:
      return hour <= 6 || hour >= 23;
  }
}

/**
 * Get chronotype-aware energy calculation
 */
export function getChronotypeAwareEnergy(
  time: Date,
  chronotype: Chronotype,
  baseEnergy: number = 50
): number {
  const hour = time.getHours();

  // Apply chronotype-specific adjustments
  switch (chronotype) {
    case Chronotype.LION:
      // Lions peak early
      if (hour >= 6 && hour <= 10) return Math.min(100, baseEnergy + 20);
      if (hour >= 14 && hour <= 18) return Math.max(0, baseEnergy - 15);
      break;

    case Chronotype.WOLF:
      // Wolves peak late
      if (hour >= 6 && hour <= 10) return Math.max(0, baseEnergy - 20);
      if (hour >= 18 && hour <= 22) return Math.min(100, baseEnergy + 20);
      break;

    case Chronotype.DOLPHIN:
      // Dolphins have irregular patterns
      if (hour >= 10 && hour <= 14) return Math.min(100, baseEnergy + 15);
      break;

    default: // BEAR
      // Bears follow natural rhythm
      if (hour >= 10 && hour <= 12) return Math.min(100, baseEnergy + 15);
      if (hour >= 14 && hour <= 16) return Math.max(0, baseEnergy - 10);
  }

  return baseEnergy;
}

/**
 * Calculate energy trend
 */
export function calculateEnergyTrend(energyLevels: EnergyLevel[]): 'increasing' | 'decreasing' | 'stable' {
  if (!energyLevels || energyLevels.length < 2) return 'stable';

  const recentLevels = energyLevels.slice(-5); // Last 5 readings
  if (recentLevels.length < 2) return 'stable';

  const first = recentLevels[0].energy || recentLevels[0].value || 0;
  const last = recentLevels[recentLevels.length - 1].energy || recentLevels[recentLevels.length - 1].value || 0;
  const difference = last - first;

  if (difference > 5) return 'increasing';
  if (difference < -5) return 'decreasing';
  return 'stable';
}

// Export energy gradients for UI compatibility
export const energyGradients = {
  power: {
    from: "from-green-50",
    to: "to-white",
  },
  focus: {
    from: "from-blue-50", 
    to: "to-white",
  },
  drift: {
    from: "from-purple-50",
    to: "to-white",
  },
};

/**
 * Get energy gradient for UI styling
 */
export function getEnergyGradient(energyLevel: number): string {
  const type = getEnergyTypeForValue(energyLevel);

  switch (type) {
    case 'power':
      return `bg-gradient-to-b ${energyGradients.power.from} ${energyGradients.power.to}`;
    case 'focus':
      return `bg-gradient-to-b ${energyGradients.focus.from} ${energyGradients.focus.to}`;
    case 'drift':
      return `bg-gradient-to-b ${energyGradients.drift.from} ${energyGradients.drift.to}`;
    default:
      return "bg-white";
  }
}
